import BoilerplateForm from './boilerplate_form';
import { html, nothing } from 'lit';
import { ref } from 'lit/directives/ref.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

export class BestieBonusForm extends BoilerplateForm {
  static get properties() {
    return {
      customer: { type: Object },
      region: { type: String },
      locales: { type: Object },
      token: { type: String },
      value: { type: String },
      code: { type: String, state: true },
      shouldPrecheckMarketing: { type: Boolean, attribute: 'should-precheck-marketing' },
      formHeadingHtml: { type: String, attribute: 'form-heading-html' },
      marketingMessageHtml: { type: String, attribute: 'marketing-message-html' },
      successTopHtml: { type: String, attribute: 'success-top-html' },
      successBottomHtml: { type: String, attribute: 'success-bottom-html' },
    };
  }

  constructor() {
    super();

    this.useUrlParams = false;
  }

  connectedCallback() {
    super.connectedCallback();
    const localCode = window.localStorage.getItem('bestieBonusCode') ?? null;
    const metafieldCode = this.customer?.bestie_code || null;
    this.code = metafieldCode || localCode || null;

    this.handleFlow();
  }

  handleFlow() {
    if (!this.code) {
      this.updateFormState('initial');
      return;
    }
    
    // Hide elements on success
    const elementsToHide = Array.from(document.querySelectorAll('#bbtcsd, #bbtcsm'));
    for (const el of elementsToHide) {
      el.style.visibility = 'hidden';
    }
    this.updateFormState('success');
  }

  async handleSubmit(event) {
    event.preventDefault();

    const fields = {
      ...this.objectData,
      acceptsMarketing: this.objectData?.acceptsMarketing
        ? true
        : false,
    };

    // TODO: remove options to ensure we actually send journey
    const body = {
      credsPath: this.region,
      fields,
      options: {
        waitForJourney: false,
      },
    };

    try {
      const response = await fetch(this.action, {
        method: this.method,
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
          'x-wf-token': this.token,
          'x-wf-value': this.value,
        },
      });

      if (!response.ok) {
        this.handleError('An unknown error has occurred');
        return;
      }

      const data = await response.json();

      if (!data.success || !data) {
        this.handleErrors(data.errors || data.error || 'An unknown error occurred');
        return;
      }

      if (!data.result?.bestieBonusCode?.code) {
        this.handleErrors('Error getting a bestie code for you!, please try again');
      }

      window.localStorage.setItem('bestieBonusCode', data.result?.bestieBonusCode?.code);
      this.code = data.result?.bestieBonusCode?.code;

      this.handleSuccess();
    } catch (error) {
      this.handleErrors(error);
    }
  }

  renderFormElements() {
    return html`

      <float-field type="email" name="email" id="${this.id}_email" label="Email" required value=${this.customer?.email || ''} error="Please enter your email" autocomplete="email" placeholder="Email"></float-field>
      
      <div class="form_split_elements">
        <float-field type="text" name="firstName" id="${this.id}_firstName" label="First Name" required value=${this.customer?.first_name || ''} error="Please enter your first name" autocomplete="given-name" placeholder="First Name"></float-field>
        <float-field type="text" name="lastName" id="${this.id}_lastName" label="Last Name" required value=${this.customer?.last_name || ''} error="Please enter your last name" autocomplete="family-name" placeholder="Last Name"></float-field>
      </div>

      <float-phone-field name="phone" id="${this.id}_phone" label="Phone" error="Please enter your phone number" autocomplete="tel" required placeholder="Phone" value=${this.customer?.phone || ''} .countryCode=${this.customer?.country_code || ''} @input=${this.handlePhoneInput}></float-phone-field>
      
      <float-checkbox type="checkbox" name="acceptsMarketing" id="${this.id}_acceptsMarketing" additional-info="${this.marketingMessageHtml}" ?checked=${this.shouldPrecheckMarketing}></float-checkbox>
    `;
  }

  renderForm() {
    return html`<form ${ref((el) => (this.form = el))} method=${ifDefined(this.method)} ?novalidate=${this.novalidate} enctype=${ifDefined(this.enctype)} class="${this.id}">${this.renderFormElements()} ${this.renderButton()} ${this.renderErrors()}</form>`;
  }

  renderInitial() {
    return html`${ this.formHeadingHtml ? unsafeHTML(this.formHeadingHtml) : nothing } ${ this.renderForm() }`;
  }

  handleSuccess() {
    this.errors = {};
    this.genericErrorSegments = [];
    this.loading = false;
    
    // Hide elements on success
    const elementsToHide = Array.from(document.querySelectorAll('#bbtcsd, #bbtcsm'));
    for (const el of elementsToHide) {
      el.style.visibility = 'hidden';
    }
    this.updateFormState('success');

    // Apply code to cart, don't care about response
    window.cartUtils.applyDiscountCode(this.code);
  }

  renderButton() {
    return html`
      <button
        type="submit"
        class="btn btn-primary"
        aria-label="Submit bestie bonus form"
        ?disabled=${this.loading}
        aria-busy=${this.loading
          ? 'true'
          : 'false'}
      >
        ${this.loading
          ? html`<bouncing-ellipsis></bouncing-ellipsis>`
          : html`${this.locales?.submit}`}
      </button>
    `;
  }

  renderSuccess() {
    // TODO: Create success design
    if (!this.code) return nothing;
    console.log('bestieCode', this.code);
    return html`
      ${this.successTopHtml ? unsafeHTML(this.successTopHtml) : nothing}
      <click-to-copy button-text="📋">${this.code}</click-to-copy>
      ${this.successBottomHtml ? unsafeHTML(this.successBottomHtml) : nothing}
    `;
  }
}

window.customElements.get('bestie-bonus-form') || window.customElements.define('bestie-bonus-form', BestieBonusForm);
